import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {forkJoin, Subscription} from "rxjs";
import {AuthService, CacheService, CompanyService, MinimalEmployee} from "@customcoding/shared";
import {AppointmentCategoryService} from "./appointment-category.service";
import {AppointmentService} from "./appointment.service";
import {RoomService} from "./room.service";
import {AntenneService} from "./antenne.service";
import {SchoolService} from "./school.service";
import {SplashScreenService} from "./splash-screen.service";
import {take} from "rxjs/operators";
import moment from "moment/moment";
import {BlockOutDatesService} from "./block-out-dates.service copy";
import {InterventionCategoryService} from "./intervention-category.service";
import {PatientService} from "./patient.service";
import {CompanyLocationsService} from "./company-locations.service";
import {AppointmentColorGroupService} from "./appointment-color-group.service";

/**
 * Service responsible for handling entity updates automatically in the background by listening to related socket channels.
 */

@Injectable({
	providedIn: 'root'
})
export class EntityPreloadService implements OnDestroy {


	private subscriptions: Subscription[] = [];
	public entityPreloadingComplete = new EventEmitter();

	private modelChannel: any;
	private lastUpdated: string;

	private preloaded: boolean = false;


	private coreModules: any;
	private scapBaseModules: any;
	private scapMainModules: any;
	private calendarModules: any;


	private _entities = [''];

	constructor(
		private auth: AuthService,
		private splashScreen: SplashScreenService,
		private cache: CacheService,
		private appointmentCategoryService: AppointmentCategoryService,
		private appointmentService: AppointmentService,//for appointment-status
		private companyService: CompanyService,
		private roomService: RoomService,
		private antenneService: AntenneService,
		private blockoutDateService: BlockOutDatesService,
		private schoolService: SchoolService, //for directions and classes
		private interventionCategoryService: InterventionCategoryService,
		private patientService: PatientService,
		private locationsService: CompanyLocationsService,
		private colorGroupService: AppointmentColorGroupService
	) {
		this.defineModules();

		this.splashScreen.show = true;

		this.auth.getCurrentUserAsync().then((user) => {
			this.splashScreen.message = "verbinde";

			MoConsole.log("user verified", user);
			this.preloadEntities();
		}, (rejected) => {
			MoConsole.log("user loggedout", rejected);
			this.splashScreen.show = false;
			this.cache.invalidateAll();
		});

		const currentUserLoggedInListener = this.auth.currentUserLoggedIn.subscribe(() => {
			MoConsole.log("user logged in!");
			this.preloaded = false;
			this.preloadEntities();
		});

		this.subscriptions.push(this.auth.loggedOut.subscribe(() => {
			this.preloaded = false;
		}));

		this.subscriptions.push(currentUserLoggedInListener);
	}

	defineModules() {

		MoConsole.log("defineModules...");

		this.coreModules = forkJoin([
			this.companyService.getAllCached(),
			this.cache.get("/api/employee/minimal/data"),
			this.cache.get('/api/employee/minimal/data?filter[disabled]=0')
		]).pipe(take(1));

		this.scapBaseModules = forkJoin([
			this.patientService.saveAllPatientsOnInit(),
			this.antenneService.getAllAntenne(),
			this.schoolService.getDirections(),
			this.locationsService.getSCAPLocationsCache() // for o-teams
		]).pipe(take(1))

		this.scapMainModules = forkJoin([
			this.cache.get('/api/scap/oteam', moment.duration(15, 'minutes')),
			this.interventionCategoryService.getAll(),
			this.cache.get<MinimalEmployee[]>('/api/scap/patients/coordinators', moment.duration(5, 'minutes')),
		]).pipe(take(1))


		this.calendarModules = forkJoin([
			this.colorGroupService.getAllCached(),
			this.appointmentCategoryService.getAll(),
			this.appointmentService.getAllStatus(),
			this.roomService.allRoomsCached(),
			this.blockoutDateService.getAllCached(),
		]).pipe(take(1))

	}

	preloadEntities() {

		if (this.preloaded) {
			MoConsole.log("entities already preloaded!");
			return;
		} else {
			this.preloaded = true;
			MoConsole.log("preload entities!");

			this.splashScreen.show = true;
			this.splashScreen.message = "lade Basis Module";

			this.subscriptions.push(
				this.coreModules.subscribe(() => {

					this.splashScreen.progress = 30;
					this.splashScreen.message = "lade Scap Module";

					this.scapBaseModules.subscribe(() => {

						this.splashScreen.progress = 60;
						this.splashScreen.message = "lade Mitarbeiter";

						this.scapMainModules.subscribe(() => {

							this.splashScreen.progress = 80;
							this.splashScreen.message = "lade Kalender";

							this.calendarModules.subscribe(() => {

								MoConsole.log("preloading complete!");
								this.splashScreen.progress = 100;
								this.splashScreen.message = " ";
								setTimeout(() => {
									this.splashScreen.show = false;
									this.entityPreloadingComplete.emit();
								}, 500);

							});
						});
					});
				})
			);

		}

	}

	faker() {

		this.splashScreen.progress = 25;
		this.splashScreen.message = "lade Basis Module";
		setTimeout(() => {
			this.splashScreen.progress = 50;
			this.splashScreen.message = "lade Scap Module";
		}, 1000);

		setTimeout(() => {
			this.splashScreen.progress = 75;
			this.splashScreen.message = "lade Mitarbeiter";
		}, 3000);

		setTimeout(() => {
			this.splashScreen.progress = 75;
			this.splashScreen.message = "lade Kalender";
		}, 4000);

		setTimeout(() => {
			this.splashScreen.progress = 100;
			this.splashScreen.message = "";
			this.splashScreen.show = false;
			this.entityPreloadingComplete.emit();

		}, 5000);

	}

	ngOnDestroy() {
		if (this.subscriptions.length > 0) {
			this.subscriptions.forEach(subscription => subscription.unsubscribe());
		}
	}


}
